import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  type Unsubscribe,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functionsClient } from "./firebase";
import type { PublicIdentitySnapshot } from "../types/identity";

export { isIdentityDmUnread, scopeIdentityDmInbox } from "./identityMessaging";

export type IdentityDmConversation = { id: string; participantIdentityIds: string[]; participantSnapshots?: Record<string, PublicIdentitySnapshot>; createdByIdentityId: string; lastMessageText: string; lastMessageAt: number; lastMessageSenderIdentityId: string | null; createdAt: number; updatedAt: number };
export type IdentityDmThread = { conversationId: string; peerIdentityId: string; peerSnapshot?: PublicIdentitySnapshot | null; lastMessageText: string; lastMessageAt: number; lastMessageSenderIdentityId: string | null; lastReadAt: number; hiddenAt: number; updatedAt: number };
export type IdentityDmMessage = { id: string; senderIdentityId: string; senderSnapshot: PublicIdentitySnapshot | null; type: "text" | "shared_post"; text: string; sharedPost?: { postId: string; authorIdentityId: string; authorSnapshot: PublicIdentitySnapshot }; createdAt: number; /** UI-only; never persisted to Firestore. */ localStatus?: "sending" | "failed" };
export type IdentityDmInbox = { identityId: string; threads: IdentityDmThread[]; peers: Record<string, PublicIdentitySnapshot> };

type StartIdentityDmResult = { conversation: IdentityDmConversation; senderSnapshot: PublicIdentitySnapshot | null; recipientSnapshot: PublicIdentitySnapshot | null };
type ListMessagesResult = { conversation: IdentityDmConversation; messages: IdentityDmMessage[] };
type ListOlderMessagesResult = { messages: IdentityDmMessage[]; hasMore: boolean };

const startIdentityDmCallable = httpsCallable<{ senderIdentityId: string; recipientIdentityId: string }, StartIdentityDmResult>(functionsClient, "startIdentityDm");
const listIdentityDmInboxCallable = httpsCallable<{ identityId: string }, IdentityDmInbox>(functionsClient, "listIdentityDmInbox");
const listIdentityDmMessagesCallable = httpsCallable<{ identityId: string; conversationId: string }, ListMessagesResult>(functionsClient, "listIdentityDmMessages");
const listIdentityDmMessagesBeforeCallable = httpsCallable<{ identityId: string; conversationId: string; beforeMessageId: string }, ListOlderMessagesResult>(functionsClient, "listIdentityDmMessagesBefore");
const sendIdentityDmMessageCallable = httpsCallable<{ conversationId: string; senderIdentityId: string; messageId: string; type?: "text" | "shared_post"; text: string; postId?: string }, { ok: true; conversationId: string; messageId: string }>(functionsClient, "sendIdentityDmMessage");
const markIdentityDmReadCallable = httpsCallable<{ identityId: string; conversationId: string }, { ok: true }>(functionsClient, "markIdentityDmRead");

const INBOX_LIMIT = 100;
const MESSAGE_LIMIT = 100;

function toMillis(value: any): number {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  return 0;
}

function snapshotFrom(value: unknown): PublicIdentitySnapshot | null {
  if (!value || typeof value !== "object") return null;
  const snapshot = value as PublicIdentitySnapshot;
  return typeof snapshot.identityId === "string" ? snapshot : null;
}

function threadFromSnapshot(docSnapshot: { id: string; data: () => Record<string, any> }): IdentityDmThread {
  const data = docSnapshot.data();
  return {
    conversationId: typeof data.conversationId === "string" ? data.conversationId : docSnapshot.id,
    peerIdentityId: typeof data.peerIdentityId === "string" ? data.peerIdentityId : "",
    peerSnapshot: snapshotFrom(data.peerSnapshot),
    lastMessageText: typeof data.lastMessageText === "string" ? data.lastMessageText : "",
    lastMessageAt: toMillis(data.lastMessageAt),
    lastMessageSenderIdentityId: typeof data.lastMessageSenderIdentityId === "string" ? data.lastMessageSenderIdentityId : null,
    lastReadAt: toMillis(data.lastReadAt),
    hiddenAt: toMillis(data.hiddenAt),
    updatedAt: toMillis(data.updatedAt),
  };
}

function peersForThreads(threads: IdentityDmThread[], fallback: Record<string, PublicIdentitySnapshot>) {
  const peers: Record<string, PublicIdentitySnapshot> = {};
  for (const thread of threads) {
    const snapshot = thread.peerSnapshot ?? fallback[thread.peerIdentityId];
    if (thread.peerIdentityId && snapshot) peers[thread.peerIdentityId] = snapshot;
  }
  return peers;
}

export async function startIdentityDm(senderIdentityId: string, recipientIdentityId: string) { return (await startIdentityDmCallable({ senderIdentityId, recipientIdentityId })).data; }
export async function listIdentityDmInbox(identityId: string) { return (await listIdentityDmInboxCallable({ identityId })).data; }
export async function listIdentityDmMessages(identityId: string, conversationId: string) { return (await listIdentityDmMessagesCallable({ identityId, conversationId })).data; }
export async function listIdentityDmMessagesBefore(identityId: string, conversationId: string, beforeMessageId: string) { return (await listIdentityDmMessagesBeforeCallable({ identityId, conversationId, beforeMessageId })).data; }
export async function sendIdentityDmMessage(input: { conversationId: string; senderIdentityId: string; messageId: string; text: string; type?: "text" | "shared_post"; postId?: string }) { return (await sendIdentityDmMessageCallable(input)).data; }
export async function markIdentityDmRead(identityId: string, conversationId: string) { return (await markIdentityDmReadCallable({ identityId, conversationId })).data; }

/**
 * Loads the initial identity-scoped inbox through the verified callable, then
 * keeps its private projection current with a Firestore listener. The callable
 * also supplies peer snapshots for older threads that predate peerSnapshot.
 */
export function subscribeIdentityDmInbox(
  identityId: string,
  onNext: (inbox: IdentityDmInbox) => void,
  onError?: (error: unknown) => void,
): Unsubscribe {
  let active = true;
  let unsubscribe: Unsubscribe | null = null;

  void listIdentityDmInbox(identityId).then((initial) => {
    if (!active) return;
    const fallbackPeers = initial.peers ?? {};
    onNext(initial);

    const inboxQuery = query(
      collection(db, "identityInboxes", identityId, "threads"),
      orderBy("updatedAt", "desc"),
      limit(INBOX_LIMIT),
    );
    unsubscribe = onSnapshot(inboxQuery, (snapshot) => {
      if (!active) return;
      const threads = snapshot.docs.map(threadFromSnapshot).filter((thread) => !thread.hiddenAt);
      onNext({ identityId, threads, peers: peersForThreads(threads, fallbackPeers) });
    }, (error) => {
      if (active) onError?.(error);
    });
  }).catch((error) => {
    if (active) onError?.(error);
  });

  return () => {
    active = false;
    unsubscribe?.();
    unsubscribe = null;
  };
}

/** Subscribe to the newest page; older history remains callable-paginated. */
export function subscribeIdentityDmMessages(
  conversationId: string,
  onNext: (messages: IdentityDmMessage[]) => void,
  onError?: (error: unknown) => void,
): Unsubscribe {
  const messagesQuery = query(
    collection(db, "directConversations", conversationId, "messages"),
    orderBy("createdAt", "desc"),
    limit(MESSAGE_LIMIT),
  );
  let active = true;
  const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
    if (!active) return;
    const messages = snapshot.docs.map((docSnapshot) => {
      const data = docSnapshot.data() as Record<string, any>;
      return {
        id: docSnapshot.id,
        senderIdentityId: typeof data.senderIdentityId === "string" ? data.senderIdentityId : "",
        senderSnapshot: snapshotFrom(data.senderSnapshot),
        type: data.type === "shared_post" ? "shared_post" as const : "text" as const,
        text: typeof data.text === "string" ? data.text : "",
        ...(data.sharedPost ? { sharedPost: data.sharedPost } : {}),
        createdAt: toMillis(data.createdAt),
      };
    }).reverse();
    onNext(messages);
  }, (error) => {
    if (active) onError?.(error);
  });

  return () => {
    active = false;
    unsubscribe();
  };
}
